// ReflectionPage.js — Student Growth Reflections
// Tabs: Prompts | Write Reflection | My Reflections | Stats & Badges

import React, { useEffect, useState } from "react";
import Layout from "../Layout";
import { Spin, message } from "antd";
import { motion } from "framer-motion";
import {
    PenLine, Lightbulb, History, Award, Send, Brain, Star,
    Flame, TrendingUp, BookOpen, ArrowLeft
} from "lucide-react";
import { api } from "../authClient";
import "./ReflectionPage.css";

// Domain colors for border accents
const DOMAIN_COLORS = {
    academics: "#1565c0", skills: "#7b1fa2", competencies: "#00838f",
    health: "#2e7d32", career: "#e65100", tech: "#283593", values: "#c62828",
};

// Growth domains for dropdown
const GROWTH_DOMAINS = {
    academics: {
        label: "Academics",
        sub_metrics: ["conceptual_clarity", "application_ability", "analytical_thinking", "critical_thinking", "interdisciplinary_thinking", "depth_vs_surface"]
    },
    skills: {
        label: "Skills",
        sub_metrics: ["communication", "collaboration", "emotional_intelligence", "self_management", "financial_literacy", "creativity"]
    },
    competencies: {
        label: "Competencies",
        sub_metrics: ["decision_quality", "leadership", "self_regulation", "reflection_maturity", "problem_solving"]
    },
    health: {
        label: "Health",
        sub_metrics: ["mental_health", "physical_fitness", "lifestyle_discipline", "nutrition_awareness", "emotional_resilience"]
    },
    career: {
        label: "Career",
        sub_metrics: ["career_awareness", "goal_setting", "professional_readiness", "industry_exposure", "networking"]
    },
    tech: {
        label: "Tech",
        sub_metrics: ["digital_literacy", "computational_thinking", "tech_creation", "data_literacy", "online_safety"]
    },
    values: {
        label: "Values",
        sub_metrics: ["honesty", "empathy", "responsibility", "courage", "fairness", "cultural_respect", "environmental_care"]
    },
};

const ReflectionPage = () => {
    const [activeTab, setActiveTab] = useState("prompts");
    const [loading, setLoading] = useState(false);

    // Prompts
    const [prompts, setPrompts] = useState([]);

    // Form
    const [formDomain, setFormDomain] = useState("");
    const [formSubMetric, setFormSubMetric] = useState("");
    const [formText, setFormText] = useState("");
    const [formProudOf, setFormProudOf] = useState("");
    const [formStruggledWith, setFormStruggledWith] = useState("");
    const [formNextStep, setFormNextStep] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [lastFeedback, setLastFeedback] = useState(null);

    // History
    const [reflections, setReflections] = useState([]);
    const [historyStats, setHistoryStats] = useState({});

    // Stats
    const [stats, setStats] = useState(null);

    // ─── Fetch on tab change ───
    useEffect(() => {
        if (activeTab === "prompts") fetchPrompts();
        if (activeTab === "history") fetchReflections();
        if (activeTab === "stats") fetchStats();
    }, [activeTab]);

    const fetchPrompts = async () => {
        setLoading(true);
        try {
            const res = await api.get("/student/reflections/prompts");
            setPrompts(res.data.prompts || []);
        } catch { message.error("Failed to load prompts"); }
        setLoading(false);
    };

    const fetchReflections = async () => {
        setLoading(true);
        try {
            const res = await api.get("/student/reflections");
            setReflections(res.data.reflections || []);
            setHistoryStats({
                streak: res.data.streak_weeks,
                total: res.data.all_time_count,
                avgDepth: res.data.avg_depth_score,
            });
        } catch { message.error("Failed to load reflections"); }
        setLoading(false);
    };

    const fetchStats = async () => {
        setLoading(true);
        try {
            const res = await api.get("/student/reflections/stats");
            setStats(res.data);
        } catch { message.error("Failed to load stats"); }
        setLoading(false);
    };

    // ─── Use prompt to prefill form ───
    const applyPrompt = (prompt) => {
        setFormDomain(prompt.domain);
        setFormSubMetric(prompt.sub_metric);
        setFormText("");
        setLastFeedback(null);
        setActiveTab("write");
    };

    // ─── Submit reflection ───
    const handleSubmit = async () => {
        if (!formDomain || !formSubMetric) {
            message.warning("Please select a domain and sub-metric");
            return;
        }
        if (formText.length < 10) {
            message.warning("Your reflection should be at least 10 characters");
            return;
        }

        setSubmitting(true);
        try {
            const res = await api.post("/student/reflections", {
                domain: formDomain,
                sub_metric: formSubMetric,
                reflection_text: formText,
                proud_of: formProudOf,
                struggled_with: formStruggledWith,
                next_step: formNextStep,
            });
            setLastFeedback(res.data);
            message.success("Reflection submitted!");
            // Clear form except domain/sub_metric
            setFormText("");
            setFormProudOf("");
            setFormStruggledWith("");
            setFormNextStep("");
        } catch (err) {
            const detail = err.response?.data?.detail || "Failed to submit";
            message.error(detail);
        }
        setSubmitting(false);
    };

    const getDepthClass = (score) => {
        if (score >= 3.5) return "high";
        if (score >= 2.0) return "medium";
        return "low";
    };

    // ─── Render: Prompts ───
    const renderPrompts = () => (
        <div>
            <div className="ref-header">
                <h1>Reflection Prompts</h1>
                <p>Based on your quiz performance, here are areas to reflect on</p>
            </div>

            {loading ? (
                <div className="ref-loading"><Spin size="large" /></div>
            ) : prompts.length === 0 ? (
                <div className="ref-empty">
                    <Lightbulb size={48} color="#ccc" />
                    <h3>No prompts available</h3>
                    <p>Complete more quizzes to get personalized reflection prompts.</p>
                </div>
            ) : (
                <div className="ref-prompts-grid">
                    {prompts.map((p, i) => (
                        <motion.div
                            key={i}
                            className="ref-prompt-card"
                            style={{ borderLeftColor: DOMAIN_COLORS[p.domain] || "#D67C2C" }}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.08 }}
                            onClick={() => applyPrompt(p)}
                        >
                            <div className="ref-prompt-domain">
                                <span className={`ref-prompt-domain-tag ${p.domain}`}>
                                    {p.domain_label || p.domain}
                                </span>
                                <span style={{ fontSize: 12, color: "#888" }}>{p.sub_metric?.replace(/_/g, " ")}</span>
                            </div>
                            <div className="ref-prompt-text">{p.prompt}</div>
                            <div className="ref-prompt-context">{p.based_on}</div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );

    // ─── Render: Write Form ───
    const renderWriteForm = () => {
        const subMetrics = formDomain ? (GROWTH_DOMAINS[formDomain]?.sub_metrics || []) : [];

        return (
            <div>
                <div className="ref-header">
                    <h1>Write a Reflection</h1>
                    <p>Take a moment to think about your learning journey</p>
                </div>

                <div className="ref-form">
                    {/* Domain + Sub-metric selectors */}
                    <div className="ref-form-row">
                        <div className="ref-form-group">
                            <label className="ref-form-label">Domain</label>
                            <select
                                className="ref-form-select"
                                value={formDomain}
                                onChange={e => { setFormDomain(e.target.value); setFormSubMetric(""); }}
                            >
                                <option value="">Select domain...</option>
                                {Object.entries(GROWTH_DOMAINS).map(([key, d]) => (
                                    <option key={key} value={key}>{d.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="ref-form-group">
                            <label className="ref-form-label">Sub-metric</label>
                            <select
                                className="ref-form-select"
                                value={formSubMetric}
                                onChange={e => setFormSubMetric(e.target.value)}
                                disabled={!formDomain}
                            >
                                <option value="">Select sub-metric...</option>
                                {subMetrics.map(sm => (
                                    <option key={sm} value={sm}>{sm.replace(/_/g, " ")}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Main reflection text */}
                    <div className="ref-form-group">
                        <label className="ref-form-label">Your Reflection</label>
                        <textarea
                            className="ref-form-textarea"
                            placeholder="Write about your learning experience, what you understood, what challenged you..."
                            value={formText}
                            onChange={e => setFormText(e.target.value)}
                            rows={5}
                        />
                    </div>

                    {/* Structured prompts */}
                    <div className="ref-form-row">
                        <div className="ref-form-group">
                            <label className="ref-form-label">What are you proud of?</label>
                            <textarea
                                className="ref-form-textarea"
                                placeholder="Something you did well..."
                                value={formProudOf}
                                onChange={e => setFormProudOf(e.target.value)}
                                rows={2}
                                style={{ minHeight: 60 }}
                            />
                        </div>
                        <div className="ref-form-group">
                            <label className="ref-form-label">What did you struggle with?</label>
                            <textarea
                                className="ref-form-textarea"
                                placeholder="An area that was difficult..."
                                value={formStruggledWith}
                                onChange={e => setFormStruggledWith(e.target.value)}
                                rows={2}
                                style={{ minHeight: 60 }}
                            />
                        </div>
                    </div>

                    <div className="ref-form-group">
                        <label className="ref-form-label">What's your next step?</label>
                        <textarea
                            className="ref-form-textarea"
                            placeholder="What will you do differently or focus on next..."
                            value={formNextStep}
                            onChange={e => setFormNextStep(e.target.value)}
                            rows={2}
                            style={{ minHeight: 60 }}
                        />
                    </div>

                    <button className="ref-submit-btn" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? <Spin size="small" /> : <Send size={16} />}
                        {submitting ? "Submitting..." : "Submit Reflection"}
                    </button>

                    {/* AI Feedback */}
                    {lastFeedback && (
                        <motion.div
                            className="ref-ai-feedback"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className={`ref-depth-score ${getDepthClass(lastFeedback.reflection_depth_score)}`}>
                                <Brain size={14} />
                                Depth Score: {lastFeedback.reflection_depth_score}/5
                            </div>
                            <h4>AI Feedback</h4>
                            <p>{lastFeedback.ai_feedback}</p>
                        </motion.div>
                    )}
                </div>
            </div>
        );
    };

    // ─── Render: History ───
    const renderHistory = () => (
        <div>
            <div className="ref-header">
                <h1>My Reflections</h1>
                <p>{historyStats.total || 0} total reflections | {historyStats.streak || 0}-week streak | Avg depth: {historyStats.avgDepth || 0}/5</p>
            </div>

            {loading ? (
                <div className="ref-loading"><Spin size="large" /></div>
            ) : reflections.length === 0 ? (
                <div className="ref-empty">
                    <History size={48} color="#ccc" />
                    <h3>No reflections yet</h3>
                    <p>Start reflecting on your growth to build your learning journal.</p>
                </div>
            ) : (
                <div className="ref-history-list">
                    {reflections.map((r, i) => (
                        <motion.div
                            key={r._id}
                            className="ref-history-card"
                            style={{ borderLeftColor: DOMAIN_COLORS[r.domain] || "#888" }}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                        >
                            <div className="ref-history-header">
                                <div className="ref-history-domain">
                                    <span style={{ color: DOMAIN_COLORS[r.domain] || "#888" }}>
                                        {r.domain} / {r.sub_metric?.replace(/_/g, " ")}
                                    </span>
                                </div>
                                <div className="ref-history-date">
                                    {r.timestamp ? new Date(r.timestamp).toLocaleDateString() : ""}
                                </div>
                            </div>
                            <div className="ref-history-text">{r.reflection_text}</div>
                            <div className="ref-history-meta">
                                {r.reflection_depth_score && (
                                    <span className={`ref-depth-score ${getDepthClass(r.reflection_depth_score)}`}>
                                        <Brain size={12} /> Depth: {r.reflection_depth_score}/5
                                    </span>
                                )}
                                {r.proud_of && <span>Proud: {r.proud_of.substring(0, 50)}...</span>}
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );

    // ─── Render: Stats & Badges ───
    const renderStats = () => {
        if (loading) return <div className="ref-loading"><Spin size="large" /></div>;
        if (!stats) return null;

        return (
            <div>
                <div className="ref-header">
                    <h1>Stats & Badges</h1>
                    <p>Track your reflection journey</p>
                </div>

                {/* Stats Row */}
                <div className="ref-stats-row">
                    <div className="ref-stat-card">
                        <div className="ref-stat-value">{stats.streak_weeks || 0}</div>
                        <div className="ref-stat-label"><Flame size={14} style={{ display: "inline", verticalAlign: "middle" }} /> Week Streak</div>
                    </div>
                    <div className="ref-stat-card">
                        <div className="ref-stat-value">{stats.total_reflections || 0}</div>
                        <div className="ref-stat-label">Total Reflections</div>
                    </div>
                    <div className="ref-stat-card">
                        <div className="ref-stat-value">{stats.quality_reflections || 0}</div>
                        <div className="ref-stat-label"><Star size={14} style={{ display: "inline", verticalAlign: "middle" }} /> Quality (3+)</div>
                    </div>
                </div>

                {/* Depth Trend */}
                {stats.depth_trend && stats.depth_trend.length > 0 && (
                    <div style={{ background: "white", borderRadius: 12, padding: 20, marginBottom: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                            <TrendingUp size={16} style={{ verticalAlign: "middle", marginRight: 6 }} />
                            Recent Depth Trend
                        </h3>
                        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", height: 80 }}>
                            {stats.depth_trend.map((d, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ height: 0 }}
                                    animate={{ height: `${(d.score / 5) * 100}%` }}
                                    transition={{ delay: i * 0.1 }}
                                    style={{
                                        flex: 1, background: d.score >= 3.5 ? "#4caf50" : d.score >= 2 ? "#ff9800" : "#f44336",
                                        borderRadius: "4px 4px 0 0", minHeight: 8, position: "relative"
                                    }}
                                    title={`${d.score}/5 on ${new Date(d.date).toLocaleDateString()}`}
                                >
                                    <div style={{ position: "absolute", top: -18, left: "50%", transform: "translateX(-50%)", fontSize: 11, fontWeight: 600 }}>
                                        {d.score}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Badges */}
                {stats.badges && stats.badges.length > 0 && (
                    <div>
                        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Your Badges</h3>
                        <div className="ref-badges-grid">
                            {stats.badges.map((badge, i) => (
                                <motion.div
                                    key={i}
                                    className="ref-badge"
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: i * 0.1 }}
                                >
                                    <div className="ref-badge-icon">
                                        <Award size={18} />
                                    </div>
                                    <div>
                                        <div className="ref-badge-text">{badge.name}</div>
                                        <div className="ref-badge-desc">{badge.description}</div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {stats.badges && stats.badges.length === 0 && (
                    <div className="ref-empty">
                        <Award size={40} color="#ccc" />
                        <h3>No badges yet</h3>
                        <p>Keep reflecting to earn your first badge!</p>
                    </div>
                )}
            </div>
        );
    };

    // ─── Main Render ───
    return (
        <Layout>
            <div className="reflection-container">
                <div className="ref-tabs">
                    <button className={`ref-tab ${activeTab === "prompts" ? "active" : ""}`} onClick={() => setActiveTab("prompts")}>
                        <Lightbulb size={16} /> Prompts
                    </button>
                    <button className={`ref-tab ${activeTab === "write" ? "active" : ""}`} onClick={() => setActiveTab("write")}>
                        <PenLine size={16} /> Write
                    </button>
                    <button className={`ref-tab ${activeTab === "history" ? "active" : ""}`} onClick={() => setActiveTab("history")}>
                        <History size={16} /> My Reflections
                    </button>
                    <button className={`ref-tab ${activeTab === "stats" ? "active" : ""}`} onClick={() => setActiveTab("stats")}>
                        <Award size={16} /> Stats
                    </button>
                </div>

                {activeTab === "prompts" && renderPrompts()}
                {activeTab === "write" && renderWriteForm()}
                {activeTab === "history" && renderHistory()}
                {activeTab === "stats" && renderStats()}
            </div>
        </Layout>
    );
};

export default ReflectionPage;
